import { useEffect, useState } from 'react';
import { api, type MenuCategory, type User } from '../api';
import { Logo } from '../components/BrandMarks';
import { LanguagePicker } from '../components/LanguagePicker';
import { useT } from '../i18n';

interface Props {
  user: User | null;
  navigate: (path: string) => void;
}

const HERO_IMAGE =
  'https://images.unsplash.com/photo-1495474472287-4d71bcdd2085?w=2000&q=80&auto=format&fit=crop';
const CONCEPT_IMAGE =
  'https://images.unsplash.com/photo-1461023058943-07fcbe16d735?w=1200&q=80&auto=format&fit=crop';
const BREWING_IMAGE =
  'https://images.unsplash.com/photo-1442512595331-e89e73853f31?w=1200&q=80&auto=format&fit=crop';

export function Landing({ user, navigate }: Props) {
  const [categories, setCategories] = useState<MenuCategory[]>([]);
  const [milkNote, setMilkNote] = useState('');
  const [menuFailed, setMenuFailed] = useState(false);
  const t = useT();

  useEffect(() => {
    api
      .menu()
      .then((res) => {
        setCategories(res.categories);
        setMilkNote(res.milkNote);
      })
      .catch(() => {
        // The rest of the page still renders; just don't leave the carta
        // heading sitting above an empty space with no explanation.
        setMenuFailed(true);
      });
  }, []);

  const goToLoyalty = () => navigate(user ? (user.role === 'staff' ? '/admin' : '/perfil') : '/login');

  return (
    <div className="screen">
      <header className="landing-header">
        <Logo title="Caffeina" />
        <nav>
          <LanguagePicker />
          {user ? (
            <button
              type="button"
              className="btn btn--pill btn--outline"
              onClick={() => navigate(user.role === 'staff' ? '/admin' : '/perfil')}
            >
              {user.role === 'staff' ? t.nav.panel : t.nav.myProfile}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn--pill btn--solid"
              onClick={() => navigate('/login')}
            >
              {t.nav.signIn}
            </button>
          )}
        </nav>
      </header>

      <div className="hero">
        <img src={HERO_IMAGE} alt={t.hero.imageAlt} />
        <div className="hero-scrim" />
        <div className="hero-body">
          <div className="hero-card">
            <div className="hero-eyebrow">{t.hero.eyebrow}</div>
            <h1 className="hero-title">
              {t.hero.titleLine1}
              <br />
              {t.hero.titleLine2}
            </h1>
            <div className="hero-actions">
              <a href="#carta" className="btn btn--hero btn--cream">
                {t.hero.seeMenu}
              </a>
              <button
                type="button"
                className="btn btn--hero btn--ghost-cream"
                onClick={goToLoyalty}
              >
                {t.hero.earnPoints}
              </button>
            </div>
          </div>
        </div>
      </div>

      <section className="section">
        <div className="split">
          <div>
            <div className="eyebrow" style={{ marginBottom: 14 }}>
              {t.concept.eyebrow}
            </div>
            <h2 className="section-title" style={{ marginBottom: 18 }}>
              {t.concept.title}
            </h2>
            <p className="body-copy">{t.concept.body}</p>
          </div>
          <div className="photo">
            <img src={CONCEPT_IMAGE} alt={t.concept.imageAlt} />
          </div>
        </div>
      </section>

      <section className="menu-section" id="carta">
        <div className="eyebrow" style={{ marginBottom: 8 }}>
          {t.menu.eyebrow}
        </div>
        <h2 className="section-title" style={{ marginBottom: 32 }}>
          {t.menu.address}
        </h2>

        <div className="menu-grid">
          {categories.map((category) => (
            <div key={category.id}>
              <div className="menu-category-name">{category.name}</div>
              {category.items.map((item) => (
                <div className="menu-item" key={item.id}>
                  <div className="menu-item-row">
                    <div className="menu-item-name">{item.name}</div>
                    <div className="menu-item-price">{item.price}</div>
                  </div>
                  {item.note && <div className="menu-item-note">{item.note}</div>}
                </div>
              ))}
            </div>
          ))}
        </div>

        {menuFailed && (
          <div className="menu-footnote">
            {t.menu.failedBefore}
            <a href="https://instagram.com/caffeina.ba" target="_blank" rel="noopener noreferrer">
              @caffeina.ba
            </a>
            {t.menu.failedAfter}
          </div>
        )}

        {milkNote && <div className="menu-footnote">{milkNote}</div>}
      </section>

      <section className="section">
        <div className="split">
          <div>
            <div className="eyebrow" style={{ marginBottom: 14 }}>
              {t.story.eyebrow}
            </div>
            <h2
              className="section-title"
              style={{ fontSize: 'clamp(24px, 2.6vw, 32px)', marginBottom: 18 }}
            >
              {t.story.title}
            </h2>
            <p className="body-copy">{t.story.body}</p>
          </div>
          <div className="photo">
            <img src={BREWING_IMAGE} alt={t.story.imageAlt} />
          </div>
        </div>
      </section>

      <section className="location">
        <div className="location-inner split">
          <div>
            <div className="eyebrow" style={{ marginBottom: 14 }}>
              {t.location.eyebrow}
            </div>
            <address className="location-lines" style={{ fontStyle: 'normal' }}>
              {t.location.address}
              <br />
              {t.location.hours}
              <br />
              <a href="https://instagram.com/caffeina.ba" target="_blank" rel="noopener noreferrer">
                @caffeina.ba
              </a>
            </address>
            <a
              className="btn btn--pill btn--outline-brown"
              style={{ marginTop: 20, display: 'inline-flex' }}
              href="https://www.google.com/maps/dir/?api=1&destination=-34.5889,-58.4407"
              target="_blank"
              rel="noopener noreferrer"
            >
              {t.location.directions}
            </a>
          </div>
          <div className="map">
            <iframe
              title={t.location.mapTitle}
              src={`https://www.google.com/maps?q=${encodeURIComponent(
                'Bonpland 943, Villa Crespo, Buenos Aires, Argentina',
              )}&z=16&output=embed`}
              loading="lazy"
              referrerPolicy="no-referrer-when-downgrade"
            />
          </div>
        </div>
      </section>

      <div className="cta-band">
        <h2>{t.cta.title}</h2>
        <button type="button" className="btn btn--cream" onClick={goToLoyalty}>
          {t.cta.button}
        </button>
      </div>

      <footer className="footer">
        <Logo />
        <div className="footer-line">
          Bonpland 943 · Villa Crespo · Buenos Aires · @caffeina.ba
        </div>
      </footer>
    </div>
  );
}
